// Notificaciones — the Mattermost webapp's account notification settings (web-only: the apps
// never edited them). All of it is the user's `notify_props`, written back MERGED through the
// worker (`PUT /users/me/patch` replaces the whole object); the email cadence is the
// `notifications/email_interval` preference. Every control saves on the click, optimistically.
import { useEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { emailIntervalChoice, emailIntervals, joinMentionKeys, normalizeKeywords, splitMentionKeys, threadsChoice } from '../../../core/accountSettings';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { InlineStatus } from '../../kit/Kit';
import { RadioRow, SettingsGroup, SwitchRow, useAccount, useSaver } from './parts';

/** Mattermost's own defaults for a key the account has never set. */
const DEFAULTS: Record<string, string> = {
  desktop: 'mention',
  desktop_threads: 'all',
  push: 'mention',
  push_threads: 'all',
  push_status: 'away',
  channel: 'true',
  first_name: 'false',
  comments: 'never',
  email: 'true',
  mention_keys: '',
};

const LEVELS = ['all', 'mention', 'none'] as const;

export function NotificationsSection() {
  const { account, loadError, reload, patch, prefs, setPrefs } = useAccount();
  const [np, setNp] = useState<Record<string, string> | null>(account?.notifyProps ?? null);
  // The server's answer replaces the optimistic map only when no other write is still in flight:
  // an earlier answer landing between two quick changes would otherwise undo the later one.
  const inFlight = useRef(0);
  useEffect(() => {
    if (account && !np) setNp(account.notifyProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const desktop = useSaver();
  const push = useSaver();
  const words = useSaver();
  const replies = useSaver();
  const email = useSaver();

  if (!np || !account) {
    return loadError ? <InlineStatus kind="error" text={t('web.settings.loadFailed')} onRetry={reload} /> : <div className="st-skeleton" aria-busy="true" />;
  }
  const v = (k: string) => np[k] ?? DEFAULTS[k] ?? '';
  const crt = threadsChoice(prefs?.config.threads, prefs?.display.collapsed_reply_threads).on;

  const write = (saver: ReturnType<typeof useSaver>, changes: Record<string, string>) => {
    const before = np;
    setNp((cur) => ({ ...(cur ?? np), ...changes }));
    saver.run(
      async () => {
        inFlight.current += 1;
        try {
          const r = await data.call('accountPatchNotifyProps', changes);
          if (r.ok && r.notifyProps) {
            patch({ notifyProps: r.notifyProps });
            if (inFlight.current === 1) setNp(r.notifyProps);
          }
          return r.ok;
        } finally {
          inFlight.current -= 1;
        }
      },
      () => setNp((cur) => (cur ? { ...cur, ...Object.fromEntries(Object.keys(changes).map((k) => [k, before[k]])) } : before)),
    );
  };

  const keys = splitMentionKeys(v('mention_keys'), account.user.username);
  const setWords = (custom: string[]) => write(words, { mention_keys: joinMentionKeys(keys.preserved, custom) });

  const interval = emailIntervalChoice(prefs?.notifications.email_interval);
  const setInterval = (value: string) => {
    if (!prefs) return;
    const before = prefs;
    setPrefs({ ...prefs, notifications: { ...prefs.notifications, email_interval: value } });
    email.run(
      () => data.call('accountSavePreferences', [{ category: 'notifications', name: 'email_interval', value }]),
      () => setPrefs(before),
    );
  };

  return (
    <>
      <SettingsGroup title={t('web.settings.notif.desktop')} save={desktop.state} footer={t('web.settings.notif.desktopHint')}>
        <div role="radiogroup" aria-label={t('web.settings.notif.desktop')}>
          {LEVELS.map((l) => (
            <RadioRow key={l} title={t(`web.settings.notif.level.${l}`)} checked={v('desktop') === l} onPick={() => write(desktop, { desktop: l })} />
          ))}
        </div>
        {crt && v('desktop') === 'mention' ? (
          <SwitchRow title={t('web.settings.notif.threads')} subtitle={t('web.settings.notif.threadsHint')} on={v('desktop_threads') === 'all'} onChange={(on) => write(desktop, { desktop_threads: on ? 'all' : 'mention' })} />
        ) : null}
      </SettingsGroup>

      <SettingsGroup title={t('web.settings.notif.push')} save={push.state} footer={t('web.settings.notif.pushHint')}>
        <div role="radiogroup" aria-label={t('web.settings.notif.push')}>
          {LEVELS.map((l) => (
            <RadioRow key={l} title={t(`web.settings.notif.level.${l}`)} checked={v('push') === l} onPick={() => write(push, { push: l })} />
          ))}
        </div>
        {crt && v('push') === 'mention' ? (
          <SwitchRow title={t('web.settings.notif.threads')} subtitle={t('web.settings.notif.threadsHint')} on={v('push_threads') === 'all'} onChange={(on) => write(push, { push_threads: on ? 'all' : 'mention' })} />
        ) : null}
      </SettingsGroup>
      {v('push') !== 'none' ? (
        <SettingsGroup title={t('web.settings.notif.pushStatus')} save={push.state}>
          <div role="radiogroup" aria-label={t('web.settings.notif.pushStatus')}>
            {(['online', 'away', 'offline'] as const).map((s) => (
              <RadioRow key={s} title={t(`web.settings.notif.pushStatus.${s}`)} checked={v('push_status') === s} onPick={() => write(push, { push_status: s })} />
            ))}
          </div>
        </SettingsGroup>
      ) : null}

      <SettingsGroup title={t('web.settings.notif.keywords')} save={words.state}>
        {account.user.firstName ? (
          <SwitchRow
            title={tf('web.settings.notif.firstName', account.user.firstName)}
            subtitle={t('web.settings.notif.firstNameHint')}
            on={v('first_name') === 'true'}
            onChange={(on) => write(words, { first_name: on ? 'true' : 'false' })}
          />
        ) : null}
        <SwitchRow title={t('web.settings.notif.channelWide')} subtitle={t('web.settings.notif.channelWideHint')} on={v('channel') === 'true'} onChange={(on) => write(words, { channel: on ? 'true' : 'false' })} />
        <KeywordEditor words={keys.custom} onChange={setWords} />
      </SettingsGroup>

      {!crt ? (
        <SettingsGroup title={t('web.settings.notif.replies')} save={replies.state}>
          <div role="radiogroup" aria-label={t('web.settings.notif.replies')}>
            {(['any', 'root', 'never'] as const).map((c) => (
              <RadioRow key={c} title={t(`web.settings.notif.replies.${c}`)} checked={v('comments') === c} onPick={() => write(replies, { comments: c })} />
            ))}
          </div>
        </SettingsGroup>
      ) : null}

      <SettingsGroup title={t('web.settings.notif.email')} save={email.state}>
        <SwitchRow title={t('web.settings.notif.emailToggle')} on={v('email') === 'true'} onChange={(on) => write(email, { email: on ? 'true' : 'false' })} />
        {v('email') === 'true' && prefs ? (
          <div role="radiogroup" aria-label={t('web.settings.notif.emailInterval')}>
            {emailIntervals.map((i) => (
              <RadioRow key={i} title={t(`web.settings.notif.email.${i}`)} checked={interval === i} onPick={() => setInterval(i)} />
            ))}
          </div>
        ) : null}
      </SettingsGroup>
    </>
  );
}

/** "Otras palabras": chips plus a field — Enter or a comma adds, × removes; each change saves. */
function KeywordEditor({ words, onChange }: { words: string[]; onChange(words: string[]): void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const next = normalizeKeywords([...words, draft]);
    setDraft('');
    if (next.length !== words.length) onChange(next);
  };
  return (
    <div className="list-row st-keywords">
      <span className="list-row-text">
        <span className="list-row-title">{t('web.settings.notif.customWords')}</span>
        <span className="list-row-subtitle">{t('web.settings.notif.customWordsHint')}</span>
        <span className="st-chips">
          {words.map((w) => (
            <span key={w} className="st-chip">
              {w}
              <button type="button" onClick={() => onChange(words.filter((x) => x !== w))} aria-label={tf('web.settings.notif.removeWord', w)} title={tf('web.settings.notif.removeWord', w)}>
                <Icon name="x" size={12} strokeWidth={2.2} />
              </button>
            </span>
          ))}
          <input
            className="st-chip-input"
            value={draft}
            placeholder={t('web.settings.notif.addWord')}
            aria-label={t('web.settings.notif.addWord')}
            onChange={(e) => {
              const value = e.target.value;
              if (!value.includes(',')) return setDraft(value);
              const next = normalizeKeywords([...words, value]);
              setDraft('');
              if (next.length !== words.length) onChange(next);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.trim()) {
                e.preventDefault();
                add();
              } else if (e.key === 'Backspace' && !draft && words.length) onChange(words.slice(0, -1));
            }}
            onBlur={() => draft.trim() && add()}
          />
        </span>
      </span>
    </div>
  );
}
